import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@/components/ui";
import LawWorksLoader from "@/components/ui/LawWorksLoader/LawWorksLoader";
import { useUser } from "@/components/UserProvider";
import { useCreateContactEmail } from "@/lib/data/react-query/contacts/contact-email";
import { retrieveContactQueryOptions } from "@/lib/data/react-query/contacts/contact";
import {
  useContactPortalAccess,
  useCreateContactPortalInvitation,
} from "@/lib/data/react-query/contacts/contact-portal-access";
import { EmailSchema } from "@/lib/data/schemas/CommonSchemaFields";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import type { APIError } from "@/lib/types/APIResponse";
import { formatContactsName } from "@/lib/utils/format";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { useState } from "react";
import toast from "react-hot-toast";

export function ContactPortalInviteDialog({
  contactId,
  open,
  onOpenChange,
}: {
  contactId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { hasActionPermission } = useUser();
  const canAddEmail = hasActionPermission(
    ACTION_PERMISSION_KEYS.contactEmail,
    ACTION_PERMISSION_VALUES.create,
  );
  const contactQuery = useQuery(retrieveContactQueryOptions(contactId));
  const portalAccess = useContactPortalAccess(contactId, true);
  const createEmail = useCreateContactEmail({ showSuccessToast: false });
  const createInvitation = useCreateContactPortalInvitation();
  const [emailChoice, setEmailChoice] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [savedEmail, setSavedEmail] = useState("");
  const [error, setError] = useState("");
  const contactPayload = contactQuery.data?.data;
  const contact =
    contactPayload && !Array.isArray(contactPayload) ? contactPayload : null;
  const emails = contact
    ? [
        ...new Set(
          contact.emails
            .map((item) => item.address?.trim())
            .filter((address): address is string => Boolean(address)),
        ),
      ]
    : [];
  const primaryEmail = contact?.emails
    .find((item) => item.isPrimary && item.address?.trim())
    ?.address?.trim();
  const email = emails.includes(emailChoice)
    ? emailChoice
    : (primaryEmail ?? (emails.length > 0 ? emails[0] : savedEmail));
  const access = portalAccess.data?.data;
  const pendingInvitation =
    access?.status === "pending" &&
    (!access.expiresAt || new Date(access.expiresAt) > new Date());
  const contactName = contact ? formatContactsName(contact) || "contact" : null;

  const sendInvitation = async () => {
    if (!contact || access?.activeConnection || pendingInvitation) {
      return;
    }
    setError("");
    let invitationEmail = email;
    if (!invitationEmail && canAddEmail) {
      const parsedEmail = EmailSchema.safeParse(newEmail);
      if (!parsedEmail.success) {
        setError(
          parsedEmail.error.issues[0]?.message ??
            "Enter a valid email address.",
        );
        return;
      }
      invitationEmail = parsedEmail.data;
      try {
        await createEmail.mutateAsync({
          contactId,
          address: invitationEmail,
          isPrimary: true,
          label: null,
        });
        setSavedEmail(invitationEmail);
      } catch (cause) {
        setError(
          isAxiosError<APIError>(cause)
            ? (cause.response?.data.meta.userMsg ??
                "The email could not be saved. Please try again.")
            : "The email could not be saved. Please try again.",
        );
        return;
      }
    }
    if (!invitationEmail) return;
    try {
      await createInvitation.mutateAsync({
        contactId,
        email: invitationEmail,
        idempotencyKey: crypto.randomUUID(),
      });
      onOpenChange(false);
      toast.success(`Portal invitation sent to ${invitationEmail}.`);
    } catch (cause) {
      setError(
        isAxiosError<APIError>(cause)
          ? (cause.response?.data.meta.userMsg ??
              "The invitation could not be sent. Please try again.")
          : "The invitation could not be sent. Please try again.",
      );
      void portalAccess.refetch();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto rounded-2xl">
        <DialogHeader>
          <DialogTitle>
            {contactName &&
            contact?.type === "individual" &&
            !access?.activeConnection &&
            !pendingInvitation
              ? `Invite ${contactName} to portal`
              : "Contact portal access"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-5 py-2">
          {contactQuery.isPending ? (
            <LawWorksLoader className="w-full" />
          ) : contactQuery.isError ? (
            <div className="space-y-3">
              <p role="alert" className="text-destructive text-sm">
                Could not load this contact.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => void contactQuery.refetch()}
              >
                Retry
              </Button>
            </div>
          ) : !contact ? (
            <p role="alert" className="text-destructive text-sm">
              Contact not found.
            </p>
          ) : (
            <>
              {contact.type !== "individual" ? (
                <p className="text-muted-foreground text-sm">
                  Portal invitations are available for individual contacts only.
                </p>
              ) : portalAccess.isPending ? (
                <Skeleton
                  role="status"
                  aria-label="Loading portal access"
                  className="h-5 w-48"
                />
              ) : portalAccess.isError ? (
                <div className="space-y-3">
                  <p role="alert" className="text-destructive text-sm">
                    Could not check portal access.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void portalAccess.refetch()}
                  >
                    Retry
                  </Button>
                </div>
              ) : access?.activeConnection ? (
                <p role="status" className="text-muted-foreground text-sm">
                  Portal access is active for{" "}
                  {access.invitedEmail ?? contactName}.
                </p>
              ) : pendingInvitation ? (
                <p role="status" className="text-muted-foreground text-sm">
                  Invitation pending for {access.invitedEmail ?? contactName}.
                </p>
              ) : emails.length === 0 ? (
                canAddEmail ? (
                  <div className="space-y-2">
                    <Label htmlFor="new-portal-invitation-email">Email</Label>
                    <Input
                      id="new-portal-invitation-email"
                      type="email"
                      value={savedEmail || newEmail}
                      disabled={Boolean(savedEmail) || createEmail.isPending}
                      onChange={(event) => {
                        setNewEmail(event.target.value);
                        setError("");
                      }}
                      placeholder="client@example.com"
                    />
                    <p className="text-muted-foreground text-sm">
                      We’ll save this email on the contact and send the portal
                      invitation.
                    </p>
                  </div>
                ) : (
                  <p className="text-muted-foreground text-sm">
                    Add an email address before inviting this contact.
                  </p>
                )
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="portal-invitation-email">Email</Label>
                  <Select value={email} onValueChange={setEmailChoice}>
                    <SelectTrigger id="portal-invitation-email">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {emails.map((address) => (
                        <SelectItem key={address} value={address}>
                          {address}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {error ? (
                <p role="alert" className="text-destructive text-sm">
                  {error}
                </p>
              ) : null}
            </>
          )}
        </div>
        {contact?.type === "individual" &&
        !portalAccess.isPending &&
        !portalAccess.isError &&
        !access?.activeConnection &&
        !pendingInvitation &&
        (emails.length > 0 || canAddEmail) ? (
          <DialogFooter>
            <Button
              type="button"
              disabled={createEmail.isPending || createInvitation.isPending}
              onClick={() => void sendInvitation()}
            >
              {createEmail.isPending || createInvitation.isPending
                ? "Sending invitation..."
                : emails.length > 0
                  ? "Send invitation"
                  : "Add email & send invitation"}
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
